import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ListItem, ListItemProps, UnorderedList, UnorderedListProps } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadPromotionMessages from "@store/Store/Data/Promotions/Handlers/LoadPromotionMessages";
import { getPromotionMessages } from "@store/Store/Data/Promotions/StorePromotionsSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { PromotionMessagesModel } from "@store/Types/ApiModels";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: StoreApplicationState) => {
    const currentPromotions = getCurrentPromotionsDataView(state)?.value ?? [];
    return {
        promotionMessages: getPromotionMessages(state),
        currentPromotions,
    };
};

const mapDispatchToProps = {
    loadPromotionMessages,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PromotionMessagesListStyles {
    wrapper?: InjectableCss;
    unorderedList?: UnorderedListProps;
    listItem?: ListItemProps;
    messageTypography?: TypographyPresentationProps;
}

export const promotionMessagesListStyles: PromotionMessagesListStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
            padding: 4px;
            background-color: ${getColor("common.accent")};
        `,
    },
    unorderedList: {
        css: css`
            list-style: disc;
        `,
    },
    listItem: {
        css: css`
            margin: 4px 0;
        `,
    },
    messageTypography: {
        variant: "p",
        underline: true,
    },
};

const styles = promotionMessagesListStyles;

const PromotionMessagesList: React.FC<Props> = ({ promotionMessages, currentPromotions, loadPromotionMessages }) => {
    React.useEffect(() => {
        loadPromotionMessages();
    }, []);

    const getMessage = React.useCallback(
        (pm: PromotionMessagesModel) => {
            const key = currentPromotions.some(p => p.id === pm.promotionId)
                ? pm.appliedMessageKey
                : pm.reminderMessageKey;

            return translate(key);
        },
        [currentPromotions],
    );

    const listItems = React.useMemo(
        () =>
            promotionMessages
                ?.map(promotionMessage => {
                    const msg = getMessage(promotionMessage);
                    return (
                        msg && (
                            <ListItem key={`${promotionMessage.promotionId}`} {...styles.listItem}>
                                <Typography {...styles.messageTypography}>{msg}</Typography>
                            </ListItem>
                        )
                    );
                })
                .filter(m => m !== ""),
        [promotionMessages, getMessage],
    );

    return listItems?.length ? (
        <StyledWrapper {...styles.wrapper}>
            <UnorderedList {...styles.unorderedList}>{listItems}</UnorderedList>
        </StyledWrapper>
    ) : (
        <></>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [],
    allowedContexts: ["CartPage"],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PromotionMessagesList),
    definition,
};

export default widgetModule;
